import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, LogOut, Instagram, Facebook } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAllContent } from '../hooks/useContent';
import CookieConsent from './CookieConsent';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const { contentData } = useAllContent();
  const [showTrucksDropdown, setShowTrucksDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Get navigation background color from content data
  const navBackgroundColor = contentData?.colours?.navigationBackground || '#2A2A2A';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowMobileMenu(false);
  };

  // Navigation links
  const navigationLinks = [
    { path: '/', label: 'Home' },
    { path: '/trucks', label: 'Trucks', hasDropdown: true },
    { path: '/hire', label: 'Vehicle Hire' },
    { path: '/repairs', label: 'Repairs' },
    { path: '/parts-catalogue', label: 'Parts' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  const adminLinks = [
    { path: '/admin', label: 'Inventory' },
    { path: '/admin/parts', label: 'Parts' },
    { path: '/content-editor', label: 'Settings' },
    { path: '/users', label: 'Users' },
    { path: '/communications', label: 'Subscribers' },
    { path: '/blog-admin', label: 'Blog' },
    { path: '/xml-feed', label: 'XML' }
  ];

  const trucksDropdownLinks = [
    { path: '/trucks', label: 'All Trucks' },
    { path: '/trucks/categories', label: 'Browse by Category' },
    { path: '/trucks?bodytype=Tractor', label: 'Tractor' },
    { path: '/trucks?bodytype=Rigid', label: 'Rigid' },
    { path: '/trucks?bodytype=Tippers', label: 'Tippers' },
    { path: '/trucks?bodytype=Specialist', label: 'Specialist' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Emergency Banner with Scrolling Animation */}
      <div className="bg-black text-white text-center py-2 sticky top-0 z-50 relative overflow-hidden">
        <div className="animate-marquee whitespace-nowrap">
          <span className="text-sm font-inter">
            Emergency 24/7 Contact - 
            <a 
              href="tel:07718738785" 
              className="text-orange-500 hover:text-orange-400 font-bold ml-1 underline transition-colors"
            >
              Call Now
            </a>
          </span>
        </div>
      </div>
      
      <style jsx global>{`
        @keyframes marquee {
          0% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        
        .animate-marquee {
          animation: marquee 12s linear infinite;
          display: inline-block;
        }
      `}</style>

      {/* Header */}
      <header 
        className={`sticky shadow-lg transition-all duration-300 ${
          isScrolled 
            ? 'backdrop-blur-lg border-b border-white/10' 
            : ''
        }`}
        style={{ 
          backgroundColor: isScrolled 
            ? `${navBackgroundColor}CC` // Add transparency for glass effect
            : navBackgroundColor,
          top: '36px',
          zIndex: 49
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_truckdealership/artifacts/595vp30e_navLogo.svg"
                alt="Lawman Commercials" 
                className="h-12 w-auto mr-6"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-2">
              {navigationLinks.map((link) => (
                <div key={link.path} className="relative">
                  {link.hasDropdown ? (
                    <div 
                      onMouseEnter={() => setShowTrucksDropdown(true)}
                      onMouseLeave={() => setShowTrucksDropdown(false)}
                      className="relative"
                    >
                      <Link 
                        to={link.path} 
                        className={`px-4 py-2 text-sm font-bold transition-colors flex items-center ${
                          location.pathname.startsWith('/trucks') 
                            ? 'text-orange-400 border border-transparent' 
                            : 'text-gray-200 hover:bg-orange-100 hover:text-orange-600 border border-transparent'
                        }`}
                      >
                        <span>{link.label}</span>
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Link>
                      
                      {/* Trucks Dropdown */}
                      {showTrucksDropdown && (
                        <div className="absolute top-full left-0 w-52 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-2">
                          {trucksDropdownLinks.map((dropdownLink) => (
                            <Link 
                              key={dropdownLink.path}
                              to={dropdownLink.path} 
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 hover:rounded font-medium transition-colors"
                            >
                              {dropdownLink.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link 
                      to={link.path} 
                      className={`px-4 py-2 text-sm font-bold transition-colors flex items-center ${
                        location.pathname === link.path || location.pathname.startsWith(link.path.replace('/', '') + '/') 
                          ? 'text-orange-400 border border-transparent' 
                          : 'text-gray-200 hover:bg-orange-100 hover:text-orange-600 hover:rounded border border-transparent'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Admin Section - Only visible when authenticated as admin */}
              {isAuthenticated && isAdmin && (
                <div className="border-l border-gray-600 pl-4 ml-2">
                  {adminLinks.map((adminLink) => (
                    <Link 
                      key={adminLink.path}
                      to={adminLink.path} 
                      className={`px-3 py-1 rounded text-xs font-bold transition-colors ml-2 ${
                        location.pathname === adminLink.path 
                          ? 'bg-orange-600 text-white border border-orange-600' 
                          : 'text-gray-300 hover:bg-orange-100 hover:text-orange-600 hover:rounded border border-gray-600'
                      }`}
                    >
                      {adminLink.label}
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="px-2 py-1 rounded text-xs text-orange-400 hover:text-orange-300 hover:rounded ml-2 border border-gray-600 hover:border-orange-500 transition-colors"
                    title="Logout"
                  >
                    <LogOut className="h-3 w-3" />
                  </button>
                </div>
              )}

              {/* Login Link - Hidden from frontend */}
              {false && !isAuthenticated && (
                <Link 
                  to="/login" 
                  className="px-4 py-2 rounded text-sm font-bold bg-orange-600 text-white hover:bg-orange-700 transition-colors flex items-center"
                >
                  Login
                </Link>
              )}
            </nav>

            {/* Contact Info & Mobile Menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex flex-col text-right">
                <div className="text-sm font-bold text-white">01530 231 235</div>
                <div className="text-xs text-gray-300">Expert Sales Team</div>
              </div>
              
              {/* Mobile menu button */}
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 transition-colors"
              >
                {showMobileMenu ? (
                  <X className="h-6 w-6 text-gray-300" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-300" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden border-t border-gray-600">
              <div className="px-2 pt-2 pb-3 space-y-1" style={{ backgroundColor: navBackgroundColor }}>
                {navigationLinks.map((link) => (
                  <div key={link.path}>
                    <Link
                      to={link.path}
                      className={`block px-3 py-2 text-base font-medium transition-colors ${
                        location.pathname === link.path
                          ? 'bg-orange-600 text-white rounded'
                          : 'text-gray-200 hover:bg-orange-100 hover:text-orange-600 hover:rounded'
                      }`}
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {link.label}
                    </Link>
                    
                    {/* Show truck categories as children of Trucks menu item */}
                    {link.hasDropdown && (
                      <div className="ml-4 space-y-1 mt-1">
                        {trucksDropdownLinks.slice(2).map((dropdownLink) => (
                          <Link
                            key={dropdownLink.path}
                            to={dropdownLink.path}
                            className="block px-3 py-1 text-sm text-gray-300 hover:bg-orange-100 hover:text-orange-600 hover:rounded transition-colors"
                            onClick={() => setShowMobileMenu(false)}
                          >
                            {dropdownLink.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Mobile Admin Section */}
                {isAuthenticated && isAdmin && (
                  <div className="border-t border-gray-600 pt-2 mt-2">
                    <div className="text-xs text-gray-400 px-3 py-1 uppercase tracking-wide">Admin</div>
                    {adminLinks.map((adminLink) => (
                      <Link
                        key={adminLink.path}
                        to={adminLink.path}
                        className={`block px-3 py-2 text-sm font-medium transition-colors ${
                          location.pathname === adminLink.path
                            ? 'bg-orange-600 text-white rounded'
                            : 'text-gray-300 hover:bg-orange-100 hover:text-orange-600 hover:rounded'
                        }`}
                        onClick={() => setShowMobileMenu(false)}
                      >
                        {adminLink.label}
                      </Link>
                    ))}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 text-sm font-medium text-orange-400 hover:bg-orange-100 hover:text-orange-600 rounded-md transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}

                {/* Mobile Login - Hidden from frontend */}
                {false && !isAuthenticated && (
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-base font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_truckdealership/artifacts/595vp30e_navLogo.svg"
                alt="Lawman Commercials" 
                className="h-8 w-auto mb-4"
              />
              <p className="text-gray-300 mb-4 font-inter">
                Your No.1 specialist for commercial vehicle sales, hire, and repair in Leicester.
                Over 30 years of experience serving the transport industry.
              </p>
              <div className="text-orange-500 font-bold">
                <p>31 Merrylees Industrial Estate</p>
                <p>Leeside, Desford, Leicester LE9 9FS</p>
              </div>
              
              {/* Social Media Links */}
              <div className="mt-6">
                <h4 className="text-lg font-bold text-orange-500 mb-3">Follow Us</h4>
                <div className="flex space-x-4">
                  <a 
                    href="https://www.instagram.com/lawmancommercialsltd/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-orange-500 transition-colors"
                    aria-label="Follow us on Instagram"
                  >
                    <Instagram className="h-6 w-6" />
                  </a>
                  <a 
                    href="https://www.facebook.com/LawmanCommercialLtd/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-orange-500 transition-colors"
                    aria-label="Follow us on Facebook"
                  >
                    <Facebook className="h-6 w-6" />
                  </a>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-bold text-orange-500 mb-4">Services</h3>
              <ul className="space-y-2 text-gray-300">
                <li>• MAN Commercial Vehicle Sales</li>
                <li>• Vehicle Hire</li>
                <li>• Parts & Repairs</li>
                <li>• Services & Maintenance</li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-bold text-orange-500 mb-4">Contact</h3>
              <div className="space-y-2 text-gray-300">
                <p><span className="font-bold">Office:</span> 01530 231 235</p>
                <p><span className="font-bold">John:</span> 07718 738 785</p>
                <p><span className="font-bold">Email:</span> sales@lawmancommercials.com</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center text-gray-400">
              <p>&copy; 2025 Lawman Commercials. All rights reserved.</p>
              <div className="flex space-x-6 mt-2 sm:mt-0">
                <Link to="/privacy-policy" className="hover:text-orange-500 transition-colors text-sm">
                  Privacy Policy
                </Link>
                <Link to="/terms-conditions" className="hover:text-orange-500 transition-colors text-sm">
                  Terms & Conditions
                </Link>
              </div>
            </div>
          </div>
          
          {/* Website Creator Credit */}
          <div className="border-t border-gray-800 mt-6 pt-4 text-center">
            <p className="text-gray-400 text-xs">
              Website by{' '}
              <a 
                href="https://www.linkedin.com/in/reece-gunningham-280a58206/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-orange-500 transition-colors"
              >
                Reece Gunningham
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* GDPR Cookie Consent */}
      <CookieConsent />
    </div>
  );
};

export default Layout;